<!-- src/components/ratings/MyRatingStats.vue -->
<template>
  <div v-if="stats" class="mb-8">
    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <!-- Seller Summary -->
      <div class="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-white">Kao prodavac</h3>
          <div class="bg-blue-500 rounded-full p-2">
            <svg
              class="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 11V7a4 4 0 00-8 0v4M8 11v6a4 4 0 008 0v-6M8 11h8"
              />
            </svg>
          </div>
        </div>
        <div class="flex items-center space-x-6">
          <div class="text-center">
            <div class="text-2xl font-bold text-green-400">
              {{ stats.seller.likes }}
            </div>
            <div class="text-sm text-gray-400">Pozitivne</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-red-400">
              {{ stats.seller.dislikes }}
            </div>
            <div class="text-sm text-gray-400">Negativne</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-blue-400">
              {{ sellerTotal }}
            </div>
            <div class="text-sm text-gray-400">Ukupno</div>
          </div>
        </div>
        <div v-if="sellerTotal > 0" class="mt-4">
          <div class="text-sm text-gray-400 mb-1">
            Pozitivnost: {{ sellerPositivePercentage }}%
          </div>
          <div class="w-full bg-gray-700 rounded-full h-2">
            <div
              class="bg-green-500 h-2 rounded-full transition-all duration-300"
              :style="{ width: sellerPositivePercentage + '%' }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Buyer Summary -->
      <div class="bg-gray-800 rounded-lg p-6 border-l-4 border-green-500">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-white">Kao kupac</h3>
          <div class="bg-green-500 rounded-full p-2">
            <svg
              class="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 11-4 0v-6m4 0V9a2 2 0 10-4 0v4.01"
              />
            </svg>
          </div>
        </div>
        <div class="flex items-center space-x-6">
          <div class="text-center">
            <div class="text-2xl font-bold text-green-400">
              {{ stats.buyer.likes }}
            </div>
            <div class="text-sm text-gray-400">Pozitivne</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-red-400">
              {{ stats.buyer.dislikes }}
            </div>
            <div class="text-sm text-gray-400">Negativne</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-green-400">
              {{ buyerTotal }}
            </div>
            <div class="text-sm text-gray-400">Ukupno</div>
          </div>
        </div>
        <div v-if="buyerTotal > 0" class="mt-4">
          <div class="text-sm text-gray-400 mb-1">
            Pozitivnost: {{ buyerPositivePercentage }}%
          </div>
          <div class="w-full bg-gray-700 rounded-full h-2">
            <div
              class="bg-green-500 h-2 rounded-full transition-all duration-300"
              :style="{ width: buyerPositivePercentage + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter Buttons -->
    <div class="flex flex-wrap gap-3 mb-6">
      <!-- Seller ratings button -->
      <button
        @click="handleRoleChange('seller')"
        class="px-5 py-3 rounded-lg text-white font-medium transition-colors flex items-center"
        :class="
          userRole === 'seller'
            ? 'bg-blue-600 hover:bg-blue-500 shadow-lg'
            : 'bg-gray-700 hover:bg-gray-600'
        "
      >
        <span>Kao prodavac</span>
        <span class="inline-flex items-center ml-3 space-x-2">
          <!-- Thumbs up -->
          <span
            @click.stop="handleFilterChange('seller', 'positive')"
            class="inline-flex items-center px-2 py-1 rounded cursor-pointer transition-colors"
            :class="{
              'bg-green-800 text-green-200':
                userRole === 'seller' && activeFilter === 'positive',
              'hover:bg-gray-600': !(
                userRole === 'seller' && activeFilter === 'positive'
              ),
            }"
          >
            <span class="mr-1">👍</span>
            <span>{{ stats.seller.likes }}</span>
          </span>

          <!-- Thumbs down -->
          <span
            @click.stop="handleFilterChange('seller', 'negative')"
            class="inline-flex items-center px-2 py-1 rounded cursor-pointer transition-colors"
            :class="{
              'bg-red-800 text-red-200':
                userRole === 'seller' && activeFilter === 'negative',
              'hover:bg-gray-600': !(
                userRole === 'seller' && activeFilter === 'negative'
              ),
            }"
          >
            <span class="mr-1">👎</span>
            <span>{{ stats.seller.dislikes }}</span>
          </span>
        </span>
      </button>

      <!-- Buyer ratings button -->
      <button
        @click="handleRoleChange('buyer')"
        class="px-5 py-3 rounded-lg text-white font-medium transition-colors flex items-center"
        :class="
          userRole === 'buyer'
            ? 'bg-green-600 hover:bg-green-500 shadow-lg'
            : 'bg-gray-700 hover:bg-gray-600'
        "
      >
        <span>Kao kupac</span>
        <span class="inline-flex items-center ml-3 space-x-2">
          <!-- Thumbs up -->
          <span
            @click.stop="handleFilterChange('buyer', 'positive')"
            class="inline-flex items-center px-2 py-1 rounded cursor-pointer transition-colors"
            :class="{
              'bg-green-800 text-green-200':
                userRole === 'buyer' && activeFilter === 'positive',
              'hover:bg-gray-600': !(
                userRole === 'buyer' && activeFilter === 'positive'
              ),
            }"
          >
            <span class="mr-1">👍</span>
            <span>{{ stats.buyer.likes }}</span>
          </span>

          <!-- Thumbs down -->
          <span
            @click.stop="handleFilterChange('buyer', 'negative')"
            class="inline-flex items-center px-2 py-1 rounded cursor-pointer transition-colors"
            :class="{
              'bg-red-800 text-red-200':
                userRole === 'buyer' && activeFilter === 'negative',
              'hover:bg-gray-600': !(
                userRole === 'buyer' && activeFilter === 'negative'
              ),
            }"
          >
            <span class="mr-1">👎</span>
            <span>{{ stats.buyer.dislikes }}</span>
          </span>
        </span>
      </button>

      <!-- Clear filter button -->
      <button
        v-if="activeFilter !== 'all'"
        @click="$emit('change-filter', 'all')"
        class="px-5 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors"
      >
        Prikaži sve ocene
      </button>
    </div>

    <!-- Active Filter Indicator -->
    <div
      v-if="activeFilter !== 'all'"
      class="mb-6 px-4 py-3 bg-gray-800 rounded-lg border-l-4"
      :class="{
        'border-green-500': activeFilter === 'positive',
        'border-red-500': activeFilter === 'negative',
      }"
    >
      <div class="flex items-center">
        <div
          class="w-2 h-2 rounded-full mr-3"
          :class="{
            'bg-green-500': activeFilter === 'positive',
            'bg-red-500': activeFilter === 'negative',
          }"
        ></div>
        <span class="text-sm text-gray-300">
          Prikazuju se samo
          <span
            class="font-medium"
            :class="{
              'text-green-400': activeFilter === 'positive',
              'text-red-400': activeFilter === 'negative',
            }"
          >
            {{ activeFilter === 'positive' ? 'pozitivne' : 'negativne' }}
          </span>
          ocene kao
          <span
            class="font-medium"
            :class="{
              'text-blue-400': userRole === 'seller',
              'text-green-400': userRole === 'buyer',
            }"
          >
            {{ userRole === 'seller' ? 'prodavac' : 'kupac' }}
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue';

interface RatingStats {
  seller: {
    likes: number;
    dislikes: number;
  };
  buyer: {
    likes: number;
    dislikes: number;
  };
}

export default defineComponent({
  name: 'MyRatingStats',
  props: {
    stats: {
      type: Object as PropType<RatingStats>,
      required: true,
    },
    userRole: {
      type: String as PropType<'seller' | 'buyer'>,
      required: true,
    },
    activeFilter: {
      type: String as PropType<'all' | 'positive' | 'negative'>,
      default: 'all',
    },
  },
  emits: ['change-role', 'change-filter'],
  setup(props, { emit }) {
    // Computed stats
    const sellerTotal = computed(
      () => props.stats.seller.likes + props.stats.seller.dislikes
    );

    const buyerTotal = computed(
      () => props.stats.buyer.likes + props.stats.buyer.dislikes
    );

    const sellerPositivePercentage = computed(() => {
      if (sellerTotal.value === 0) return 0;
      return Math.round((props.stats.seller.likes / sellerTotal.value) * 100);
    });

    const buyerPositivePercentage = computed(() => {
      if (buyerTotal.value === 0) return 0;
      return Math.round((props.stats.buyer.likes / buyerTotal.value) * 100);
    });

    // Event handlers
    const handleRoleChange = (role: 'seller' | 'buyer') => {
      emit('change-role', role);
      emit('change-filter', 'all');
    };

    const handleFilterChange = (
      role: 'seller' | 'buyer',
      filter: 'positive' | 'negative'
    ) => {
      if (props.userRole !== role) {
        emit('change-role', role);
      }
      emit('change-filter', filter);
    };

    return {
      sellerTotal,
      buyerTotal,
      sellerPositivePercentage,
      buyerPositivePercentage,
      handleRoleChange,
      handleFilterChange,
    };
  },
});
</script>
